// src/admin/FormEditor/utils/formValidation.js

const RESERVED_FORM_IDS = [
  "new",
  "edit",
  "delete",
  "index",
  "admin",
  "test",
  "draft",
  "regform",
];

export const validateFormId = (formId) => {
  const errors = [];

  if (!formId) {
    errors.push("Form ID is required");
    return errors;
  }

  const validFormIdPattern = /^[a-zA-Z0-9][a-zA-Z0-9_-]{2,}$/;

  if (!validFormIdPattern.test(formId)) {
    errors.push(
      "Form ID must start with a letter or number and contain only letters, numbers, hyphens, or underscores (minimum 3 characters)"
    );
  }

  if (RESERVED_FORM_IDS.includes(formId.toLowerCase())) {
    errors.push(`Form ID "${formId}" is a reserved word and cannot be used`);
  }

  return errors;
};

export const validateFormMetadata = (formMetadata) => {
  // NOTE: please update backend logic accordingly if this function changes
  // form_collector/submission_handler.py
  const errors = [];
  const fieldNames = new Set();
  const reportHeaders = new Set();
  const appliedReportHeaders = new Set();

  // Validate form ID
  const formIdErrors = validateFormId(formMetadata.form_id);
  errors.push(...formIdErrors);

  const determineAppliedReportHeader = (question, option) => {
    let appliedQuestionHeader;

    if (
      question.custom_report_header &&
      question.custom_report_header.trim() !== ""
    ) {
      appliedQuestionHeader = question.custom_report_header.trim();
    } else if ("en" in formMetadata.supported_languages) {
      appliedQuestionHeader = question.title["en"];
    } else {
      appliedQuestionHeader =
        question.title[formMetadata.supported_languages[0]];
    }

    if (!option) {
      return appliedQuestionHeader;
    } else {
      let optionDesc;
      if (
        option.custom_report_header &&
        option.custom_report_header.trim() !== ""
      ) {
        optionDesc = option.custom_report_header.trim();
      } else if ("en" in formMetadata.supported_languages) {
        optionDesc = option.desc["en"];
      } else {
        optionDesc = option.desc[formMetadata.supported_languages[0]];
      }
      const appliedHeader = `${appliedQuestionHeader} - ${optionDesc}`;
      return appliedHeader;
    }
  };

  const checkReportHeader = (appliedHeader, location) => {
    if (appliedHeader) {
      if (appliedReportHeaders.has(appliedHeader)) {
        errors.push(`${location}: Duplicate report header "${appliedHeader}"`);
      } else {
        appliedReportHeaders.add(appliedHeader);
      }
    }
  };

  const validateQuestionFieldNames = (questions, parentPath = "") => {
    questions.forEach((question, index) => {
      const questionPath = `${parentPath}Question #${index}`;

      const fieldName = question.field_name;
      if (fieldNames.has(fieldName)) {
        errors.push(`${questionPath}: Duplicate field name "${fieldName}"`);
      } else {
        fieldNames.add(fieldName);
      }

      const appliedHeader = determineAppliedReportHeader(question);
      checkReportHeader(appliedHeader, `${questionPath}`);

      if (question.type === "checkboxgroup") {
        question.options?.forEach((option, optionIndex) => {
          const appliedHeader = determineAppliedReportHeader(question, option);
          checkReportHeader(
            appliedHeader,
            `${questionPath} Option #${optionIndex}`
          );
          const optionFieldName = `${fieldName}_${optionIndex}`;
          if (fieldNames.has(optionFieldName)) {
            errors.push(
              `${questionPath}: Duplicate checkbox option field name "${optionFieldName}"`
            );
          } else {
            fieldNames.add(optionFieldName);
          }
        });
      }

      if (question.type === "list_text" && question.max_count) {
        for (let i = 0; i < question.max_count; i++) {
          const indexedFieldName = `${fieldName}_${i}`;
          if (fieldNames.has(indexedFieldName)) {
            errors.push(
              `${questionPath}: Duplicate list item field name "${indexedFieldName}"`
            );
          } else {
            fieldNames.add(indexedFieldName);
          }
        }
      }

      if (question.type === "list_subform" && question.questions) {
        validateQuestionFieldNames(
          question.questions,
          `${questionPath} Subform: `
        );
      }
    });
  };

  // Validate basic form properties
  if (!formMetadata.form_id) {
    errors.push("Form ID is required");
  }

  // Validate valid from/to dates
  if (formMetadata.valid_from?.time && formMetadata.valid_to?.time) {
    const fromDate = new Date(formMetadata.valid_from.time);
    const toDate = new Date(formMetadata.valid_to.time);
    if (fromDate > toDate) {
      errors.push("Valid from date must be before valid to date");
    }
  }

  // Validate email settings
  if (formMetadata.sendEmail ?? true) {
    const emailFieldName = formMetadata.sendEmailFieldName;

    if (!emailFieldName || emailFieldName.trim() === "") {
      errors.push(
        "Email field name is required when 'Send Email to Applicant' is enabled"
      );
    } else {
      // Check if the email field exists in questions
      const emailFieldExists = fieldNames.has(emailFieldName.trim());

      if (!emailFieldExists && formMetadata.questions) {
        // Build field names set first
        const tempFieldNames = new Set();
        const collectFieldNames = (questions) => {
          questions.forEach((question) => {
            tempFieldNames.add(question.field_name);
            if (question.type === "list_subform" && question.questions) {
              collectFieldNames(question.questions);
            }
          });
        };
        collectFieldNames(formMetadata.questions);

        if (!tempFieldNames.has(emailFieldName.trim())) {
          errors.push(
            `Email field name "${emailFieldName}" does not exist in the form questions. Please add a question with this field name or update the email field name.`
          );
        }
      }

      // Optionally: validate that the referenced field is a text type suitable for email
      if (formMetadata.questions) {
        const findQuestion = (questions, fieldName) => {
          for (const question of questions) {
            if (question.field_name === fieldName) {
              return question;
            }
            if (question.type === "list_subform" && question.questions) {
              const found = findQuestion(question.questions, fieldName);
              if (found) return found;
            }
          }
          return null;
        };

        const emailQuestion = findQuestion(
          formMetadata.questions,
          emailFieldName.trim()
        );
        if (emailQuestion) {
          const validEmailTypes = ["text", "email"];
          if (!validEmailTypes.includes(emailQuestion.type)) {
            errors.push(
              `Email field "${emailFieldName}" must be a text or email input type (currently: ${emailQuestion.type})`
            );
          }

          // Check if the field has email validation pattern
          const emailPattern =
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{1,}$/;
          if (
            emailQuestion.validation?.regex?.pattern &&
            emailQuestion.validation.regex.pattern !== emailPattern.source
          ) {
            errors.push(
              `Warning: Email field "${emailFieldName}" should have email validation pattern for best results`
            );
          }
        }
      }
    }
  }

  // Validate questions
  if (formMetadata.questions) {
    // Check field name uniqueness across all questions
    validateQuestionFieldNames(formMetadata.questions);

    // Other existing validations
    formMetadata.questions.forEach((question, index) => {
      // Validate min/max length for text inputs
      if (question.type === "text" || question.type === "textarea") {
        const min = parseInt(question.validation?.min_length ?? 0);
        const max = parseInt(question.validation?.max_length ?? 0);
        if (max > 0 && min > max) {
          errors.push(
            `Question #${index}: Minimum length (${min}) cannot be greater than maximum length (${max})`
          );
        }
      }

      // Validate min/max selections for multiple choice
      if (question.type === "checkboxgroup") {
        const min = parseInt(question.validation?.min_selections ?? 0);
        const max = parseInt(question.validation?.max_selections ?? 0);
        if (max > 0 && min > max) {
          errors.push(
            `Question #${index}: Minimum selection (${min}) cannot be greater than maximum selection (${max})`
          );
        }
        if (max > question.options?.length) {
          errors.push(
            `Question #${index}: Maximum selection (${max}) cannot be greater than number of options (${question.options?.length})`
          );
        }
      }

      // Validate options
      if (["checkboxgroup", "radio", "dropdown"].includes(question.type)) {
        if (!question.options?.length) {
          errors.push(`Question #${index}: Must have at least one option`);
        }
      }
    });
  }

  return errors;
};
